<template>
  <div>
    <div :class="props.compact ? 'tw:flex tw:gap-2' : 'tw:flex tw:gap-4'">
      <!-- Avatar -->
      <div>
        <template v-if="registration.avatar_url && noImage === false">
          <img
            :onerror="() => {noImage = true}"
            :src="registration.avatar_url"
            :alt="registration.lastname + ' ' + registration.firstname"
            :class="props.compact ? 'tw:size-8 tw:rounded-full' : 'tw:size-12 tw:rounded-full'"
          />
        </template>
        <template v-else>
          <CircleUserRound
            :alt="registration.lastname + ' ' + registration.firstname"
            stroke-width="1"
            :class="props.compact ? 'tw:size-8 tw:text-zinc-500' : 'tw:size-12 tw:text-zinc-500'"
          />
        </template>
      </div>

      <!-- Info -->
      <div class="tw:flex-1">
        <!-- Name, status and link -->
        <div class="tw:inline-flex tw:items-center tw:gap-1">
          <component
            :is="props.compact ? 'span' : 'h3'"
            :class="props.compact ? 'tw:font-bold' : 'tw:text-base tw:m-0'"
          >
            <WordHighlighter :query="props.highlight" :split-by-space="true">
              {{ registration.firstname }} {{ registration.lastname }}
            </WordHighlighter>
            <RegistrationStatus
              v-if="!props.compact"
              :status="registration.status"
              class="tw:inline-block"
            ></RegistrationStatus>
          </component>
          <div v-if="!props.compact && registration.links?.show">
            <a
              :href="registration.links.show"
              target="_blank"
              class="tw:text-blue-600 p-1 rounded tw:hover:bg-zinc-100 tw:inline-flex tw:items-center tw:gap-1"
            >
              <span class="tw:sr-only">$t("Edit")</span>
              <SquareArrowOutUpRight class="tw:size-3" />
            </a>
          </div>
        </div>

        <!-- Caption, company, email, phone -->
        <div v-if="!props.compact && registration.caption" class="tw:text-zinc-500">
          {{ registration.caption }}
        </div>

        <div v-if="registration.company" class="tw:text-zinc-500">
          {{ registration.company }}
        </div>
        <div v-if="registration.email" class="tw:text-zinc-500">
          {{ registration.email }}
        </div>
        <div v-if="registration.phone" class="tw:text-zinc-500">
          {{ registration.phone }}
        </div>

        <!-- Barcode -->
        <div
          v-if="!props.compact && registration.barcode"
          class="tw:flex tw:items-center tw:gap-1.5 tw:text-zinc-500"
        >
          <QrCode class="tw:size-4" />
          <code>{{ registration.barcode }}</code>
        </div>

        <!-- Tags -->
        <div>
          <span v-for="tag in registration.tags" :key="tag.id" class="badge badge-pill badge-secondary  text-truncate mr-2 mt-2">
            <i class="fa fa-tag" aria-hidden="true"></i>
            {{ tag.name.fr ??  tag.name.en ?? tag.name ?? 'N/A' }}
          </span>
        </div>

        <!-- Deleted registration -->
        <template v-if="registration.deleted_at">
          <div class="badge badge-danger tw:inline-flex tw:flex-none tw:items-center tw:gap-1">
            <Trash2 class="tw:size-3" />
            <div>{{ $t("Registration deleted") }}</div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from "vue";
import { QrCode, SquareArrowOutUpRight, CircleUserRound, Trash2 } from "@lucide/vue";
import RegistrationStatus from "@/components/registration/RegistrationStatus.vue";
// Imported rather than taken from the global registration, which only the backoffice bundle has.
import WordHighlighter from "vue-word-highlighter";

const noImage = ref(false);

const props = withDefaults(defineProps<{
  registration: object;
  /**
   * Fits the block into a table row: a smaller avatar, the name as plain text rather
   * than a heading, and the lines a listing does not read -- the caption, the barcode
   * and the registration's own status -- left out.
   */
  compact?: boolean;
  /** A search term to pick out of the name, when the block stands in a list of results. */
  highlight?: string;
}>(), {
  compact: false,
  highlight: '',
});
</script>
